import React from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { patternFillProceduralShader } from "../../../components/FloorPlan/PatternFillMaterial";
import { FillPattern } from "../../../types/floorplannerTypes";
import { getFillPatternDefinition } from "../../../utils/fillPatterns";

export const PATTERN_LINE_DARKNESS = 0.62;

export type LitPatternCoordinateSpace = "uv" | "world-triplanar";

export type LitPatternShaderConfig = {
  worldScale: number;
  strokeWidth: number;
  shaderType: number;
};

type LitPatternMaterialProps = {
  fillColor: THREE.ColorRepresentation;
  fillPattern: FillPattern;
  attach?: string;
  coordinateSpace?: LitPatternCoordinateSpace;
  uvChannel?: 0 | 1;
  roughness?: number;
  metalness?: number;
  side?: THREE.Side;
  opacity?: number;
  transparent?: boolean;
  depthWrite?: boolean;
  depthTest?: boolean;
  polygonOffset?: boolean;
  polygonOffsetFactor?: number;
  polygonOffsetUnits?: number;
};

export function getLitPatternShaderConfig(
  pattern: FillPattern,
): LitPatternShaderConfig | null {
  const definition = getFillPatternDefinition(pattern);
  if (!definition) return null;

  return {
    worldScale: definition.worldScale,
    strokeWidth: definition.strokeWidth,
    shaderType: definition.shaderType,
  };
}

export function getLitPatternWorldScale(pattern: FillPattern): number {
  return getLitPatternShaderConfig(pattern)?.worldScale ?? 1;
}

export function getLitPatternLineFactor(): number {
  return 1 - PATTERN_LINE_DARKNESS;
}

type PatternShader = Parameters<NonNullable<THREE.Material["onBeforeCompile"]>>[0];

export function applyLitPatternUniforms(
  uniforms: PatternShader["uniforms"],
  config: LitPatternShaderConfig,
): void {
  const setUniform = (name: string, value: unknown) => {
    if (uniforms[name]) {
      uniforms[name].value = value;
    } else {
      uniforms[name] = { value };
    }
  };

  setUniform("patternScale", config.worldScale);
  setUniform("strokeWidth", config.strokeWidth);
  setUniform("patternType", config.shaderType);
  setUniform("patternTexture", null);
  setUniform("usePatternTexture", false);
  setUniform("lineDarkness", PATTERN_LINE_DARKNESS);
}

export function applyLitPatternShader(
  shader: PatternShader,
  config: LitPatternShaderConfig,
  coordinateSpace: LitPatternCoordinateSpace,
  uvChannel: 0 | 1,
): void {
  applyLitPatternUniforms(shader.uniforms, config);

  const usesWorldCoordinates = coordinateSpace === "world-triplanar";
  const uvAttribute = uvChannel === 1 ? "uv1" : "uv";
  const vertexDeclarations = usesWorldCoordinates
    ? "varying vec3 vFloorplannerPatternWorldPosition;"
    : `${uvChannel === 1 ? "attribute vec2 uv1;\n" : ""}varying vec2 vPatternCoord;`;
  const vertexAssignment = usesWorldCoordinates
    ? `
      vec4 floorplannerPatternWorldPosition = vec4(transformed, 1.0);
      #ifdef USE_BATCHING
        floorplannerPatternWorldPosition =
          batchingMatrix * floorplannerPatternWorldPosition;
      #endif
      #ifdef USE_INSTANCING
        floorplannerPatternWorldPosition =
          instanceMatrix * floorplannerPatternWorldPosition;
      #endif
      floorplannerPatternWorldPosition =
        modelMatrix * floorplannerPatternWorldPosition;
      vFloorplannerPatternWorldPosition =
        floorplannerPatternWorldPosition.xyz;
    `
    : `vPatternCoord = ${uvAttribute};`;
  const fragmentCoordinates = usesWorldCoordinates
    ? `
      varying vec3 vFloorplannerPatternWorldPosition;
      vec2 vPatternCoord;
    `
    : "varying vec2 vPatternCoord;";
  const setPatternCoordinates = usesWorldCoordinates
    ? `
      vec3 floorplannerPatternNormal = abs(normalize(cross(
        dFdx(vFloorplannerPatternWorldPosition),
        dFdy(vFloorplannerPatternWorldPosition)
      )));
      if (
        floorplannerPatternNormal.z >= floorplannerPatternNormal.x &&
        floorplannerPatternNormal.z >= floorplannerPatternNormal.y
      ) {
        vPatternCoord = vFloorplannerPatternWorldPosition.xy;
      } else if (floorplannerPatternNormal.y >= floorplannerPatternNormal.x) {
        vPatternCoord = vFloorplannerPatternWorldPosition.xz;
      } else {
        vPatternCoord = vFloorplannerPatternWorldPosition.yz;
      }
    `
    : "";

  shader.vertexShader = shader.vertexShader.replace(
    "#include <common>",
    `#include <common>\n${vertexDeclarations}`,
  );

  if (usesWorldCoordinates) {
    shader.vertexShader = shader.vertexShader.replace(
      "#include <worldpos_vertex>",
      `#include <worldpos_vertex>\n${vertexAssignment}`,
    );
  } else {
    shader.vertexShader = shader.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>\n${vertexAssignment}`,
    );
  }

  shader.fragmentShader = shader.fragmentShader
    .replace(
      "#include <common>",
      `#include <common>
        uniform float patternScale;
        uniform float strokeWidth;
        uniform int patternType;
        uniform sampler2D patternTexture;
        uniform bool usePatternTexture;
        uniform float lineDarkness;
        ${fragmentCoordinates}
        ${patternFillProceduralShader}
      `,
    )
    .replace(
      "#include <map_fragment>",
      `#include <map_fragment>
        ${setPatternCoordinates}
        vec2 floorplannerPatternUv = vPatternCoord / patternScale;
        float floorplannerPatternHalfWidth = strokeWidth / patternScale;
        float floorplannerPatternMask = getPatternMask(
          patternType,
          floorplannerPatternUv,
          floorplannerPatternHalfWidth
        );
        diffuseColor.rgb *= 1.0 - lineDarkness * clamp(
          floorplannerPatternMask,
          0.0,
          1.0
        );
      `,
    );
}

const LitPatternMaterial: React.FC<LitPatternMaterialProps> = ({
  fillColor,
  fillPattern,
  attach = "material",
  coordinateSpace = "uv",
  uvChannel = 0,
  roughness = 0.78,
  metalness = 0,
  side = THREE.DoubleSide,
  opacity = 1,
  transparent = false,
  depthWrite = true,
  depthTest = true,
  polygonOffset = false,
  polygonOffsetFactor,
  polygonOffsetUnits,
}) => {
  const invalidate = useThree((state) => state.invalidate);
  const compiledShaderRef = React.useRef<PatternShader | null>(null);
  const config = React.useMemo(
    () => getLitPatternShaderConfig(fillPattern),
    [fillPattern],
  );
  const handleBeforeCompile = React.useCallback<
    THREE.Material["onBeforeCompile"]
  >(
    (shader) => {
      if (config) {
        compiledShaderRef.current = shader;
        applyLitPatternShader(shader, config, coordinateSpace, uvChannel);
      }
    },
    [config, coordinateSpace, uvChannel],
  );
  const getProgramCacheKey = React.useCallback(
    () => `floorplanner-lit-pattern-v4-${coordinateSpace}-uv${uvChannel}`,
    [coordinateSpace, uvChannel],
  );

  React.useLayoutEffect(() => {
    if (!config || !compiledShaderRef.current) return;

    applyLitPatternUniforms(compiledShaderRef.current.uniforms, config);
    invalidate();
  }, [config, invalidate]);

  React.useEffect(
    () => () => {
      compiledShaderRef.current = null;
    },
    [],
  );

  if (!config) return null;

  return (
    <meshStandardMaterial
      attach={attach}
      color={fillColor}
      roughness={roughness}
      metalness={metalness}
      side={side}
      opacity={opacity}
      transparent={transparent}
      depthWrite={depthWrite}
      depthTest={depthTest}
      polygonOffset={polygonOffset}
      polygonOffsetFactor={polygonOffsetFactor}
      polygonOffsetUnits={polygonOffsetUnits}
      onBeforeCompile={handleBeforeCompile}
      customProgramCacheKey={getProgramCacheKey}
      onUpdate={(material) => {
        material.side = side;
      }}
    />
  );
};

export default LitPatternMaterial;
